import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import BreedPicker from "@/components/BreedPicker";
import { toast } from "sonner";

/**
 * Owner-initiated pedigree backfill — add a single ancestor (sire OR dam)
 * to a registered dog. Submission goes to the admin review queue; on
 * approval, the ancestor appears on the pedigree tree as a lightweight
 * "Pedigree-only" record.
 *
 * When ``resubmitId`` is provided, the dialog operates in resubmit mode:
 * pre-fills with the original entry from the bounced submission and
 * issues a PUT /resubmit instead of a fresh POST.
 */
export default function AddPedigreeAncestorDialog({
  open,
  onClose,
  dogId,
  dogName,
  defaultRelation = "sire",
  initialEntry = null,
  resubmitId = null,
  onSubmitted,
}) {
  const fromInitial = initialEntry || {};
  const startRelation = fromInitial.relation || defaultRelation;
  const blank = (relation) => ({
    name: fromInitial.name || "",
    breed: fromInitial.breed || "",
    sex: fromInitial.sex || (relation === "sire" ? "male" : "female"),
    dob: fromInitial.dob || "",
    color: fromInitial.color || "",
    microchip: fromInitial.microchip || "",
    external_registration: fromInitial.external_registration || "",
    notes: fromInitial.notes || "",
  });
  const [relation, setRelation] = useState(startRelation);
  const [form, setForm] = useState(blank(startRelation));
  const [submitting, setSubmitting] = useState(false);

  // Re-seed form whenever the dialog opens with a different initialEntry.
  React.useEffect(() => {
    if (!open) return;
    setRelation(startRelation);
    setForm(blank(startRelation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialEntry?.entry_id, resubmitId]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const reset = () => {
    setRelation(defaultRelation);
    setForm({
      name: "",
      breed: "",
      sex: defaultRelation === "sire" ? "male" : "female",
      dob: "",
      color: "",
      microchip: "",
      external_registration: "",
      notes: "",
    });
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        attached_dog_id: dogId,
        entries: [
          {
            entry_id: "root",
            relation,
            parent_entry_id: null,
            name: form.name.trim(),
            sex: form.sex || (relation === "sire" ? "male" : "female"),
            breed: form.breed.trim(),
            dob: form.dob.trim(),
            color: form.color.trim(),
            microchip: form.microchip.trim(),
            external_registration: form.external_registration.trim(),
            notes: form.notes.trim(),
          },
        ],
      };
      const { data } = resubmitId
        ? await api.put(`/pedigree-additions/${resubmitId}/resubmit`, payload)
        : await api.post("/pedigree-additions", payload);
      toast.success(resubmitId ? "Resubmitted for admin review" : "Submitted for admin review");
      reset();
      onSubmitted?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md" data-testid="add-ancestor-dialog">
        <DialogHeader>
          <DialogTitle>
            {resubmitId
              ? `Resubmit ancestor for ${dogName}`
              : `Add ancestor to ${dogName}'s pedigree`}
          </DialogTitle>
          <DialogDescription>
            {resubmitId
              ? "Apply the admin's requested changes below. Submitting will replace the previous entries on this submission and send it back for review."
              : "Submit a known sire or dam. A registry admin will review and approve the addition. Most fields are optional — a name is enough to start."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 block">
              Relation to {dogName}
            </Label>
            <RadioGroup
              value={relation}
              onValueChange={(v) => {
                setRelation(v);
                set("sex", v === "sire" ? "male" : "female");
              }}
              className="grid grid-cols-2 gap-2"
            >
              <Label
                htmlFor="rel-sire"
                className={`border rounded-lg p-3 cursor-pointer text-sm font-semibold text-center ${
                  relation === "sire" ? "border-[#0E0E0E] bg-[#FAFAFA]" : "border-[#E5E5E5]"
                }`}
              >
                <RadioGroupItem value="sire" id="rel-sire" className="sr-only" />
                Sire (father)
              </Label>
              <Label
                htmlFor="rel-dam"
                className={`border rounded-lg p-3 cursor-pointer text-sm font-semibold text-center ${
                  relation === "dam" ? "border-[#0E0E0E] bg-[#FAFAFA]" : "border-[#E5E5E5]"
                }`}
              >
                <RadioGroupItem value="dam" id="rel-dam" className="sr-only" />
                Dam (mother)
              </Label>
            </RadioGroup>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Name *
            </Label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="mt-1 rounded-lg"
              data-testid="ancestor-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                DOB (approx OK)
              </Label>
              <Input
                value={form.dob}
                onChange={(e) => set("dob", e.target.value)}
                placeholder="2018 or 2018-06-15"
                className="mt-1 rounded-lg"
                data-testid="ancestor-dob"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Color
              </Label>
              <Input
                value={form.color}
                onChange={(e) => set("color", e.target.value)}
                className="mt-1 rounded-lg"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Breed
              </Label>
              <div className="mt-1">
                <BreedPicker
                  value={form.breed}
                  onChange={(v) => set("breed", v)}
                  placeholder="Pick a breed…"
                  testId="ancestor-breed-picker"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Microchip
              </Label>
              <Input
                value={form.microchip}
                onChange={(e) => set("microchip", e.target.value)}
                className="mt-1 rounded-lg"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              External registration # (if any)
            </Label>
            <Input
              value={form.external_registration}
              onChange={(e) => set("external_registration", e.target.value)}
              className="mt-1 rounded-lg"
              data-testid="ancestor-ext-reg"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Notes (for admin reviewer)
            </Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="mt-1 rounded-lg"
              placeholder="Titles, pedigree source, etc."
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="ancestor-submit-btn"
          >
            {submitting
              ? (resubmitId ? "Resubmitting…" : "Submitting…")
              : (resubmitId ? "Resubmit for review" : "Submit for review")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
